import { GUserInputDesc } from "./input";
import { GStack, GetVec2, Vector2, Vector3 } from "./types";
import { MathClamp, MathIntersectionRayAABB, MathIntersectionSphereSphere } from "./utils";
import { GScreenDesc } from "./screenDesc";
import { SceneBase } from "../scenes/scene";
import { TransformFromNDCToWorld } from "../helpers/transform";

///Screen Space Interactable GUI allowing to spatially control such Data as Position/Velocity
export class SpatialControlPoint {
    bEnabled = true;

    Radius;

    PositionViewSpace;

    PositionViewSpacePrev;

    PositionNDCSpace = GetVec2(0, 0);

    bDragState;

    bIntersectionThisFrame;

    bIntersectionPrevFrame;

    bSelectedThisFrame: boolean;

    bReleasedThisFrame = false;

    MinBoundsNDC = GetVec2(-1.0, -1.0);

    MaxBoundsNDC = GetVec2(1.0, 1.0);

    private bDraggable: boolean;

    constructor(initialPosition: Vector2, inRadius: number, inbIsDraggable: boolean) {
        this.Radius = inRadius;
        this.PositionViewSpace = initialPosition;
        this.PositionViewSpacePrev = initialPosition;
        this.PositionNDCSpace.x = this.PositionViewSpace.x / GScreenDesc.ScreenRatio;
        this.PositionNDCSpace.y = this.PositionViewSpace.y;

        this.bDragState = false;
        this.bIntersectionThisFrame = false;
        this.bIntersectionPrevFrame = false;
        this.bSelectedThisFrame = false;
        this.bDraggable = inbIsDraggable;
    }

    ClearState() {
        this.bSelectedThisFrame = false;
        this.bIntersectionPrevFrame = false;
        this.bIntersectionThisFrame = false;
    }

    OnUpdate() {
        this.bSelectedThisFrame = false;
        this.bReleasedThisFrame = false;
        this.bIntersectionPrevFrame = this.bIntersectionThisFrame;
        this.bIntersectionThisFrame = false;
        //Construct client pointer intersection sphere
        const clientSphereRadius = 0.01; //might switch to ray
        const clientSpherePosViewSpace = {
            x: GUserInputDesc.InputPosCurNDC.x * GScreenDesc.ScreenRatio,
            y: GUserInputDesc.InputPosCurNDC.y,
        };

        if (!this.bDragState || !this.bDraggable) {
            //check if client intersects
            this.bIntersectionThisFrame = MathIntersectionSphereSphere(
                clientSpherePosViewSpace,
                clientSphereRadius,
                this.PositionViewSpace,
                this.Radius,
            );

            if (GUserInputDesc.bPointerInputPressedCurFrame && this.bIntersectionThisFrame) {
                this.bSelectedThisFrame = true;
                this.bDragState = true && this.bDraggable;
            }
        } //drag state
        else {
            this.bIntersectionThisFrame = true;
            this.PositionViewSpacePrev.x = this.PositionViewSpace.x;
            this.PositionViewSpacePrev.y = this.PositionViewSpace.y;

            this.PositionViewSpace = clientSpherePosViewSpace;

            this.PositionViewSpace.x = MathClamp(
                this.PositionViewSpace.x,
                this.MinBoundsNDC.x * GScreenDesc.ScreenRatio,
                this.MaxBoundsNDC.x * GScreenDesc.ScreenRatio,
            );
            this.PositionViewSpace.y = MathClamp(this.PositionViewSpace.y, this.MinBoundsNDC.y, this.MaxBoundsNDC.y);

            this.PositionViewSpace.x = MathClamp(
                this.PositionViewSpace.x,
                -GScreenDesc.ScreenRatio + this.Radius,
                GScreenDesc.ScreenRatio - this.Radius,
            );
            this.PositionViewSpace.y = MathClamp(this.PositionViewSpace.y, -1 + this.Radius, 1 - this.Radius);

            /* if (!this.bSelectedThisFrame) {
                this.bDragState = false;
            } */
            /* const thres = 0.05;
            const inputOutOfBounds =
                GUserInputDesc.InputPosCurNDC.x > 1.0 - thres ||
                GUserInputDesc.InputPosCurNDC.x < thres ||
                GUserInputDesc.InputPosCurNDC.y > 1.0 - thres ||
                GUserInputDesc.InputPosCurNDC.y < thres; */
            if (!GUserInputDesc.bPointerInputPressedCurFrame /* || inputOutOfBounds */) {
                this.bDragState = false;
                this.bReleasedThisFrame = true;
            }
        }

        this.PositionNDCSpace.x = this.PositionViewSpace.x / GScreenDesc.ScreenRatio;
        this.PositionNDCSpace.y = this.PositionViewSpace.y;
    }
}

export class SpatialControlPointWS {
    readonly PositionWS = new Vector3(0, 0, 0);
    BoundsExtent = new Vector3(1, 1, 1);

    readonly IntersectionPosWS = new Vector3(0, 0, 0);
    readonly IntersectionPosOffset = new Vector3(0, 0, 0);
    IntersectionDepth = 0;

    bDragState = false;

    bIntersectionThisFrame = false;

    bIntersectionPrevFrame = false;

    bSelectedThisFrame = false;

    bReleasedThisFrame = false;

    constructor(inPos: Vector3) {
        this.PositionWS.Set(inPos);
    }

    OnUpdate() {
        this.bSelectedThisFrame = false;
        this.bReleasedThisFrame = false;
        this.bIntersectionPrevFrame = this.bIntersectionThisFrame;
        this.bIntersectionThisFrame = false;

        //Construct pointer ray
        const rayOrigin = GStack.TempVec3;
        rayOrigin.Set(SceneBase.UniformBuffer.Params.CameraPosition);
        //rayOrigin.z -= 0.1;

        if (!this.bDragState) {
            const rayDir = GStack.TempVec3_2;
            TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, 20, rayDir);
            rayDir.Negate(rayOrigin);
            rayDir.Normalize();

            const preciseIntersectPos = GStack.TempVec3_3;

            //check if client intersects
            this.bIntersectionThisFrame = MathIntersectionRayAABB(
                rayOrigin,
                rayDir,
                this.PositionWS,
                this.BoundsExtent,
                preciseIntersectPos,
            );

            //preciseIntersectPos.z += 0.2;

            if (this.bIntersectionThisFrame) {
                this.IntersectionPosWS.Set(preciseIntersectPos);
            }

            if (GUserInputDesc.bPointerInputPressedCurFrame && this.bIntersectionThisFrame) {
                this.bSelectedThisFrame = true;
                this.bDragState = true;
                this.IntersectionDepth = preciseIntersectPos.z;
                this.IntersectionPosOffset.Set(this.PositionWS);
                this.IntersectionPosOffset.Negate(preciseIntersectPos);
            }
        } //drag state
        else {
            this.bIntersectionThisFrame = true;

            //get cur pointer pos
            const pointerPosWS = GStack.TempVec3_3;
            TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, this.IntersectionDepth - rayOrigin.z, pointerPosWS);

            this.PositionWS.Set(pointerPosWS);
            this.PositionWS.Add(this.IntersectionPosOffset);

            if (!GUserInputDesc.bPointerInputPressedCurFrame) {
                this.bDragState = false;
                this.bReleasedThisFrame = true;
            }
        }
    }
}
